import React from 'react';
import { Tooltip } from '../Tooltip';
import { Icon } from '../icons/Icon';
import Avatar from '../Avatar';
import type { IntlShape } from 'react-intl';

export interface UserMenuProps {
  intl: IntlShape;
  user?: {
    image?: string;
    picture?: string;
    name?: string;
  } | null;
  isMobileMenuOpen?: boolean;
  setIsMobileMenuOpen?: (open: boolean) => void;
  className?: string;
}

/**
 * User menu button component for navigation
 * Opens the mobile menu drawer with user options
 */
export const UserMenu: React.FC<UserMenuProps> = ({
  intl,
  user,
  isMobileMenuOpen,
  setIsMobileMenuOpen,
  className = '',
}) => {
  const handleClick = () => {
    const willOpen = !(isMobileMenuOpen ?? false);
    setIsMobileMenuOpen?.(willOpen);
  };

  return (
    <Tooltip
      content={intl.formatMessage({
        id: 'tooltip.menu',
        defaultMessage: 'Open menu to access saved jobs, profile, and settings',
        description: 'Tooltip text for menu button that opens user menu drawer',
      })}
      position="bottom"
    >
      <button
        className={`flex items-center gap-2 py-1.5 rounded-full bg-white/60 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors cursor-pointer ${
          user && (user.image || user.picture) ? 'pl-1 pr-2' : 'px-2'
        } ${className}`}
        onClick={handleClick}
        aria-label={intl.formatMessage({
          id: 'navigation.menu',
          defaultMessage: 'Menu',
          description: 'Button aria-label to open navigation menu',
        })}
      >
        {user && (user.image || user.picture) ? (
          <>
            <div className="flex-shrink-0 overflow-hidden rounded-full">
              <Avatar
                src={user.image || user.picture}
                size={32}
                name={user.name || 'User'}
              />
            </div>
            <span className="text-gray-700 dark:text-gray-300 flex-shrink-0">
              <Icon name="bars3" useMaskForColor className="w-5 h-5" />
            </span>
          </>
        ) : (
          <span className="text-gray-700 dark:text-gray-300">
            <Icon name="bars3" useMaskForColor className="w-6 h-6" />
          </span>
        )}
      </button>
    </Tooltip>
  );
};
